import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Send, ShieldAlert } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const ST = { sent: C.green, skipped: C.slate, failed: C.red };

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

export default function RenewalNudgesPage() {
  const api = useApi();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const canEdit = ['mga_admin', 'platform_owner'].includes(user?.role);

  const load = useCallback(() => {
    api.get('/renewals/nudges').then(r => setData(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async (patch) => {
    try {
      const cfg = { ...data.config, ...patch };
      await api.put('/renewals/nudges/config', { enabled: cfg.enabled, test_mode: cfg.test_mode });
      setData(d => ({ ...d, config: cfg }));
      toast.success('Nudge settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}</div>;

  return (
    <div className="space-y-5" data-testid="renewal-nudges-page">
      <div>
        <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Renewal Nudges</h2>
        <p className="text-sm mt-0.5" style={{ color: C.slate }}>The moment an offer lands, the policyholder is emailed their secure portal link — accepting takes one tap.</p>
      </div>

      <Card className="p-5 rounded-sm bg-white flex flex-wrap items-center gap-8" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="nudge-config-card">
        <label className="flex items-center gap-3">
          <Switch checked={data.config.enabled} onCheckedChange={v => canEdit && saveConfig({ enabled: v })} disabled={!canEdit} data-testid="nudge-enabled-switch" />
          <div>
            <p className="text-sm font-medium" style={{ color: C.navy }}>Nudges {data.config.enabled ? 'on' : 'off'}</p>
            <p className="text-[11px]" style={{ color: C.slate }}>Fires automatically on every offer — manual or ARIA auto-offer</p>
          </div>
        </label>
        <label className="flex items-center gap-3">
          <Switch checked={data.config.test_mode} onCheckedChange={v => canEdit && saveConfig({ test_mode: v })} disabled={!canEdit} data-testid="nudge-testmode-switch" />
          <div>
            <p className="text-sm font-medium" style={{ color: C.navy }}>Test mode {data.config.test_mode ? 'on' : 'off'}</p>
            <p className="text-[11px]" style={{ color: C.slate }}>Routes every nudge to the safe inbox instead of the client's real address</p>
          </div>
        </label>
        {data.config.test_mode && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-sm" style={{ background: 'rgba(232,160,32,0.08)', border: '1px solid rgba(232,160,32,0.3)' }}>
            <ShieldAlert className="w-4 h-4" style={{ color: C.amber }} />
            <p className="text-[11px]" style={{ color: '#8A5A00' }}>Test mode is on — no client receives a real email. Switch off only once live client emails are on file.</p>
          </div>
        )}
      </Card>

      <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="nudge-log">
        <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
          <Overline>Last {data.nudges.length} nudges</Overline>
        </div>
        {data.nudges.length === 0 ? (
          <div className="py-10 text-center">
            <Send className="w-7 h-7 mx-auto mb-2" style={{ color: C.slate }} />
            <p className="text-sm" style={{ color: C.slate }}>No nudges yet — the next renewal offer will trigger one automatically.</p>
          </div>
        ) : data.nudges.map(n => (
          <div key={n.nudge_id} className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-b last:border-0" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
            <div className="min-w-0">
              <span className="text-sm font-medium" style={{ color: C.navy }}>{n.business_name || '—'}</span>
              <p className="text-[11px]" style={{ color: C.slate }}>
                {n.status === 'sent'
                  ? <>to {n.to}{n.test_mode && n.intended ? <> (intended: {n.intended})</> : null}</>
                  : n.reason}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize" style={{ background: `${ST[n.status] || C.slate}12`, color: ST[n.status] || C.slate }}>{n.status}</span>
              <span className="text-[11px]" style={{ color: C.slate }}>{new Date(n.at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
